import type { ReactNode } from "react";

const baseCard = "rounded-lg border border-border bg-card shadow-soft";

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

export function PageShell({ children }: { children: ReactNode }): JSX.Element {
  return <div className="flex min-h-full flex-col gap-6">{children}</div>;
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        {description ? <div className="text-sm text-muted-foreground">{description}</div> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Tabs({
  tabs,
  activeId,
  onChange,
}: {
  tabs: Array<{ id: string; label: string; status?: "validated" | "pending" }>;
  activeId: string;
  onChange: (id: string) => void;
}): JSX.Element {
  return (
    <div className={cx(baseCard, "flex items-center overflow-hidden")}>
      {tabs.map((tab) => {
        const hasStatus = tab.status != null;
        const isValidated = tab.status === "validated";
        const activeClass = hasStatus
          ? isValidated
            ? "bg-emerald-600 text-white"
            : "bg-white text-foreground ring-2 ring-primary"
          : "bg-primary text-primary-foreground";
        const inactiveClass = hasStatus
          ? isValidated
            ? "bg-emerald-50 text-emerald-700"
            : "bg-white text-muted-foreground hover:bg-muted hover:text-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground";
        return (
          <button
            key={tab.id}
            type="button"
            className={cx(
              "px-4 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              tab.id === activeId ? activeClass : inactiveClass,
            )}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function ProgressIndicator({
  steps,
}: {
  steps: Array<{ label: string; validated: boolean }>;
}): JSX.Element {
  const total = steps.length;
  const completed = steps.filter((step) => step.validated).length;
  const percent = total ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <div className="h-2 w-32 rounded-full bg-muted">
        <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${percent}%` }} />
      </div>
      <span>
        Gates validated {completed}/{total}
      </span>
    </div>
  );
}

export function Pane({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={cx(baseCard, "flex h-full flex-col", className)}>{children}</div>;
}

export function PaneHeader({ title, meta, actions }: { title: string; meta?: ReactNode; actions?: ReactNode }): JSX.Element {
  return (
    <div className="sticky top-0 z-10 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-foreground">{title}</div>
          {meta ? <div className="mt-1 text-xs text-muted-foreground">{meta}</div> : null}
        </div>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="border-b border-border px-4 py-4 last:border-b-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="mt-2 text-sm text-foreground">{children}</div>
    </div>
  );
}

export function Field({ label, value }: { label: string; value?: ReactNode }): JSX.Element {
  return (
    <div className="grid gap-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground">{value ?? "—"}</div>
    </div>
  );
}

export function Badge({ label, tone = "neutral" }: { label: string; tone?: "neutral" | "success" | "warning" }): JSX.Element {
  const toneClasses =
    tone === "success"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : tone === "warning"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-muted text-muted-foreground border-border";
  return <span className={cx("rounded-full border px-2 py-0.5 text-xs font-medium", toneClasses)}>{label}</span>;
}

export function Tag({ label }: { label: string }): JSX.Element {
  return <span className="rounded-full bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">{label}</span>;
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {description ? <div className="mt-2 text-xs text-muted-foreground">{description}</div> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <div className="space-y-3 px-4 py-4">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-3 w-full animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}

export function Divider(): JSX.Element {
  return <div className="h-px w-full bg-border" />;
}

export function IconButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
    >
      {icon}
    </button>
  );
}
